import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'next-i18next';
import { ComponentProps, ReactNode, useEffect, useState } from 'react';

import { usePagination } from '@boss/hooks';
import { IAccounPriceInfo, IArticle, ISearchArticle, SimpleColor } from '@boss/types/b2b-b2c';
import { Alert, ArticleCard, Button, Pagination, Presence } from '@boss/ui';

import Loader from './LoadingAnimation';
import { ColorPickerModal } from '../';
import { ArticleCardWrapper } from '../../components';
import { COLOR_TYPE } from '../../constants';
import { useColors, usePrice } from '../../hooks';
import { articleCardVariant, getPackagingOptions } from '../../utils';

export type ExtentedArticle = (ISearchArticle | IArticle) & {
  selectedQuantity?: number;
  selectedPackaging?: string;
  selectedColor?: SimpleColor;
  salesQuantity?: number;
  salesUnit?: string;
  slug?: string;
  imageSrc?: string | null;
  price?: {
    netInclVat: number;
    netExclVat: number;
    salesPrice: number;
  };
  discount?: number;
  discountInfo?: string;
  lineId: string;
};

interface StockEntry {
  status: 'available' | 'insufficient' | 'unavailable' | null;
  label: string | null;
}

interface Props {
  accountPriceInfo?: IAccounPriceInfo[];
  articles: ExtentedArticle[];
  discountConditions?: Record<string, string[]>;
  editableArticles?: boolean;
  getConfirmLabel?: (numberOfSelectedResults: number) => string;
  hideArticleLabels?: boolean;
  hidePrice?: boolean;
  isLoading?: boolean;
  isUpdating?: boolean;
  itemsPerPage?: number;
  onConfirmSelection?: (articles: ExtentedArticle[]) => void;
  onRemove?: (lineId: string) => void;
  onUpdateColor?: (lineId: string, color: SimpleColor) => void;
  onUpdatePackaging?: (lineId: string, packaging: string) => void;
  onUpdateQuantity?: (lineId: string, quantity: number) => void;
  onUpdateResults?: (articles: ExtentedArticle[]) => void;
  onValidationChange?: (isValid: boolean) => void;
  showSelectAllButton?: boolean;
  stockLabels?: Record<string, StockEntry | undefined>;
  validateAllArticles?: boolean;
}

// ponytail: stock labels rendered inline below each article card
const stockDotClass = (status: string) => {
  switch (status) {
    case 'available':
      return 'bg-green';
    case 'insufficient':
      return 'bg-yellow-500';
    default:
      return 'bg-red';
  }
};

// [46232] Stock label node rendered inside the card under the article name/color
// (via the ArticleCard bottomContent slot) instead of below the card. The name
// column is narrow (it wraps long names), so the label is whitespace-nowrap and
// may extend into the card's empty middle space rather than wrap.
const stockLabelNode = (stock: StockEntry | undefined): ReactNode =>
  stock?.status && stock?.label ? (
    <div className="small flex items-center gap-2 whitespace-nowrap" role="status">
      <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${stockDotClass(stock.status)}`} />
      <span className={stockTextClass(stock.status)}>{stock.label}</span>
    </div>
  ) : undefined;

const stockTextClass = (status: string) => {
  switch (status) {
    case 'available':
      return 'text-green';
    case 'insufficient':
      return 'text-yellow-500';
    default:
      return 'text-red';
  }
};

const PagedArticles = ({
  accountPriceInfo,
  articles,
  discountConditions,
  editableArticles,
  getConfirmLabel,
  hideArticleLabels = false,
  hidePrice,
  isLoading,
  isUpdating,
  itemsPerPage = 5,
  onConfirmSelection: confirmSelection,
  onRemove: handleRemove,
  onUpdateColor: handleUpdateColor,
  onUpdatePackaging: handleUpdatePackaging,
  onUpdateQuantity: handleUpdateQuantity,
  onUpdateResults,
  onValidationChange,
  showSelectAllButton = false,
  stockLabels,
  validateAllArticles = false,
}: Props) => {
  const { showPrice: _showPrice } = usePrice();
  const showPrice = _showPrice && !hidePrice;
  const { showColors } = useColors({});
  const { t } = useTranslation('common');
  const itemsSelectable = !!confirmSelection;
  const { currentPage, paginate, currentItems, totalPages } = usePagination<ExtentedArticle>(itemsPerPage, articles);
  const [selectedResults, _setSelectedResults] = useState<ExtentedArticle[]>([]);
  const [showColorPickerForArticle, setShowColorPickerForArticle] = useState<ExtentedArticle | null>(null);
  const [showColorSelectionWarning, setShowColorSelectionWarning] = useState(false);
  const [allArticlesSelected, setAllArticlesSelected] = useState(false);
  const [lastValiditySentToParent, setLastValiditySentToParent] = useState<boolean | undefined>(undefined);

  const isSelected = (item: ExtentedArticle) => {
    return selectedResults.some(selectedItem => selectedItem.lineId === item.lineId);
  };

  const setSelectedResults = (results: ExtentedArticle[]) => {
    setAllArticlesSelected(results.length === articles.length);
    _setSelectedResults(results);
    onUpdateResults?.(results);
  };

  const updateQuantity = (article: ExtentedArticle, quantity: number) => {
    handleUpdateQuantity?.(article.lineId, quantity);

    if (isSelected(article)) {
      setSelectedResults(
        selectedResults.map(selectedItem => {
          if (selectedItem.lineId === article.lineId) {
            return { ...selectedItem, selectedQuantity: quantity };
          }
          return selectedItem;
        }),
      );
    }
  };

  const updatePackaging = (article: ExtentedArticle, packaging: string) => {
    handleUpdatePackaging?.(article.lineId, packaging);

    if (isSelected(article)) {
      setSelectedResults(
        selectedResults.map(selectedItem => {
          if (selectedItem.lineId === article.lineId) {
            return { ...selectedItem, selectedPackaging: packaging };
          }
          return selectedItem;
        }),
      );
    }
  };

  const toggleSelectAllResults = () => (allArticlesSelected ? setSelectedResults([]) : setSelectedResults(articles));

  const toggleResult = (item: ExtentedArticle) => {
    if (isSelected(item)) {
      setSelectedResults(selectedResults.filter(selectedItem => selectedItem.lineId !== item.lineId));
    } else {
      setSelectedResults([...selectedResults, item]);
    }
  };

  const updateColor = (item: ExtentedArticle, color: SimpleColor) => {
    if (!isSelected(item)) {
      setSelectedResults([...selectedResults, { ...item, selectedColor: color }]);
    } else {
      setSelectedResults(
        selectedResults.map(selectedItem => {
          if (selectedItem.lineId === item.lineId) {
            return { ...selectedItem, selectedColor: color };
          }

          return selectedItem;
        }),
      );
    }

    if (handleUpdateColor) {
      handleUpdateColor(item.lineId, color);
    }
  };

  const onRemove = (id: string) => {
    setSelectedResults(selectedResults.filter(selectedItem => selectedItem.lineId !== id));
    handleRemove?.(id);
  };

  const validateSelection = () => {
    const isValid = selectedResults.every(article => {
      const isMyMxArticle = article.colortypegroups.includes(COLOR_TYPE.MYMX);

      return !isMyMxArticle || !!article.selectedColor?.id;
    });

    if (!isValid) {
      setShowColorSelectionWarning(true);
    }

    return isValid;
  };

  useEffect(() => {
    const everyMymxArticleHasColor = articles
      .filter(article => article.colortypegroups.includes(COLOR_TYPE.MYMX))
      .every(article => !!article.selectedColor?.id);
    const noMymxArticlesPresent = !articles.some(article => article.colortypegroups.includes(COLOR_TYPE.MYMX));
    const isOverallValid = noMymxArticlesPresent || everyMymxArticleHasColor;

    if (isOverallValid !== lastValiditySentToParent) {
      onValidationChange?.(isOverallValid);
      setLastValiditySentToParent(isOverallValid);
    }

    if (validateAllArticles) {
      setShowColorSelectionWarning(!isOverallValid);
    } else {
      // Interactive selection (e.g., wishlist)
      if (showColorSelectionWarning) {
        const currentSelectedMymxAreValid = selectedResults
          .filter(article => article.colortypegroups.includes(COLOR_TYPE.MYMX))
          .every(article => !!article.selectedColor?.id);

        if (currentSelectedMymxAreValid) {
          setShowColorSelectionWarning(false);
        }
      }
    }
  }, [
    articles,
    selectedResults,
    validateAllArticles,
    showColorSelectionWarning,
    onValidationChange,
    lastValiditySentToParent,
  ]);

  useEffect(() => {
    // If the current page is empty, go back one page
    if (!currentItems?.length && articles.length && currentPage > 0) {
      paginate(currentPage - 1);
    }
  }, [currentItems?.length, articles.length, currentPage, paginate]);

  const articleCardTranslations: ComponentProps<typeof ArticleCard>['translations'] = t('articleCard', {
    returnObjects: true,
  });

  const getPrice = (article: ExtentedArticle, quantity: number) => {
    const unitsForSelectedPackaging =
      article.packaging?.units?.find(unit => unit?.unit === article.selectedPackaging)?.quantity ?? 1;

    const accountPrice = accountPriceInfo?.find(price => price?.id === article.id);

    if (article.price) {
      return article.price;
    }

    const unitPrice = accountPriceInfo ? accountPrice?.netunitprice : article.pricing?.price ?? 0;

    const price = unitPrice ?? 0 * quantity;
    const totalPrice = quantity * unitsForSelectedPackaging * price;

    return {
      netInclVat: totalPrice,
      netExclVat: totalPrice,
      salesPrice: totalPrice,
    };
  };

  const articlesToValidate = validateAllArticles ? articles : selectedResults;
  const invalidArticles = articlesToValidate.filter(
    article => article.colortypegroups.includes(COLOR_TYPE.MYMX) && !article.selectedColor?.id,
  );

  return (
    <Presence
      className="flex flex-col gap-5"
      id="paged-articles-presence"
      isLoading={isLoading}
      loader={<Loader />}
      visible
    >
      {showColorSelectionWarning && (
        <Alert type="warning">
          <div className="mb-2">
            {t(validateAllArticles ? 'selectColorWarning' : 'list.createNewList.selectColorWarning', {
              count: invalidArticles.length,
              ns: validateAllArticles ? 'basket' : 'account',
            })}
          </div>
          <ul>
            {invalidArticles.map(article => (
              <li key={article.lineId}>{article.name}</li>
            ))}
          </ul>
        </Alert>
      )}
      {showSelectAllButton && (
        <Button
          className="my-5 ml-auto py-2"
          label={allArticlesSelected ? t('articleCard.clearSelection') : t('articleCard.selectAll')}
          onClick={itemsSelectable ? () => toggleSelectAllResults() : undefined}
          testId="select-all-results"
          type="primary"
        />
      )}
      {currentItems?.map(article => {
        const quantity = article.selectedQuantity || 1;
        // Stock labels are keyed by the API-resolved stockproductnumber (echoed as line.id).
        const stock = stockLabels?.[article.stockproductnumber];

        return (
          <div key={article.lineId}>
            <ArticleCardWrapper
              article={article}
              bottomContent={stockLabelNode(stock)}
              color={
                article.selectedColor?.code
                  ? {
                      name: article.selectedColor.name
                        ? `${article.selectedColor.code} - ${article.selectedColor.name}`
                        : article.selectedColor.code,
                      rgb: article.selectedColor.rgb,
                    }
                  : undefined
              }
              deleted={article.itemdeleted}
              disabled={isUpdating}
              discount={article.discount}
              discountConditions={discountConditions?.[article.id]}
              discountInfo={article.discountInfo}
              hideLabels={hideArticleLabels}
              imageSrc={article.imageSrc}
              isPaint={article?.isPaint}
              isUpdating={isUpdating}
              onColorSelect={showColors ? () => setShowColorPickerForArticle(article) : undefined}
              onRemove={handleRemove && (() => onRemove(article.lineId))}
              onSelect={itemsSelectable ? () => toggleResult(article) : undefined}
              onUpdatePackaging={handleUpdatePackaging && (packaging => updatePackaging(article, packaging))}
              onUpdateQuantity={handleUpdateQuantity && (quantity => updateQuantity(article, quantity))}
              packaging={article.selectedPackaging}
              packagingOptions={getPackagingOptions(article)}
              price={showPrice ? getPrice(article, quantity) : undefined}
              quantity={quantity}
              selected={isSelected(article)}
              showColorPicker={article?.colortypegroups?.includes(COLOR_TYPE.MYMX)}
              slug={article.slug}
              translations={articleCardTranslations}
              type={editableArticles ? 'extended' : 'default'}
              variant={articleCardVariant}
            />
          </div>
        );
      })}

      {showColorPickerForArticle && (
        <ColorPickerModal
          articleId={showColorPickerForArticle.id}
          onClose={() => setShowColorPickerForArticle(null)}
          selectedColor={showColorPickerForArticle.selectedColor}
          setSelectedColor={color => {
            updateColor(showColorPickerForArticle, color);
          }}
        />
      )}
      <div className="flex flex-col items-center justify-between md:flex-row">
        {totalPages > 1 && <Pagination currentPage={currentPage} onPaginate={paginate} totalPages={totalPages} />}
        {!!selectedResults.length && (
          <Button
            className="my-5 ml-auto py-2"
            icon={faPlus}
            iconPosition="left"
            label={
              getConfirmLabel
                ? getConfirmLabel(selectedResults.length)
                : t('articleCard.addResults', { count: selectedResults.length })
            }
            onClick={
              itemsSelectable
                ? () => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    if (validateSelection()) {
                      setSelectedResults([]);
                      confirmSelection(selectedResults);
                      setShowColorSelectionWarning(false);
                    }
                  }
                : undefined
            }
            testId="add-results"
            type="primary"
          />
        )}
      </div>
    </Presence>
  );
};

export default PagedArticles;
